import { emptyFragment } from '@execonline-inc/execonline-ui';
import { observer } from 'mobx-react';
import { useNavigate } from 'react-router-dom';
import { currentUserStore } from '../../../../CurrentUserStore';
import { findLinkBy } from '../../../../Links';
import { T } from '../../../../Translations';
import { trackIntercomEvent } from '../../../../services/intercomTrackingService';
import CollectionBuilderTile from './CollectionBuilderTile';

interface Props {
  context: 'Home' | 'Learning Collections';
}

function CollectionBuilder({ context }: Props) {
  const navigate = useNavigate();

  const v1Link = findLinkBy({ rel: 'new-learning-collection' }, currentUserStore.links);
  const v2Link = findLinkBy({ rel: 'new-learning-collection-template-v2' }, currentUserStore.links);

  const openV2Builder = () => {
    trackIntercomEvent(`${context} - Create a Collection - Collection Builder v2`);
    navigate('/learning_collections/builder_v2');
  };

  const openClassicBuilder = () => {
    trackIntercomEvent(`${context} - Create a Collection - Collection Builder`);
    navigate('/learning_collections/new');
  };

  const handleTileClick = () => {
    v2Link.cata({ Just: openV2Builder, Nothing: openClassicBuilder });
  };

  const handleClassicBuilderClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    openClassicBuilder();
  };

  // "Use Classic Builder" is a v1 fallback for the "both grants present"
  // state — only render it when the user actually has access to both.
  // v2 orgs hide v1 by default (via the show_builder_v1 feature switch);
  // the fallback would 404 for those users otherwise.
  const classicBuilderFallback = v1Link
    .andThen(() => v2Link)
    .map(() => (
      <button
        type="button"
        onClick={handleClassicBuilderClick}
        className="mt-2 text-xs text-muted-foreground underline hover:text-foreground"
        data-intercom-target="create-a-collection-classic-builder"
      >
        <T kind="Use Classic Builder" />
      </button>
    ))
    .getOrElse(emptyFragment);

  const enabledTile = (
    <CollectionBuilderTile
      customClassName="border-[#DEDEDE] bg-[#FCFCFC] hover:bg-[#F2F2F3] hover:border-[#C6C6C6]"
      src="/builder-icon.png"
      TitleColor="black"
      descColor="dark-gray"
      onClick={handleTileClick}
      bottomSlot={classicBuilderFallback}
    />
  );

  return v1Link
    .map(() => enabledTile)
    .orElse(() => v2Link.map(() => enabledTile))
    .getOrElse(() => (
      <CollectionBuilderTile
        customClassName="border-[#dedede4c] bg-[#FAFAFA] cursor-no-drop"
        src="/builder-icon-light.png"
        TitleColor="light-gray"
        descColor="gray"
      />
    ));
}

export default observer(CollectionBuilder);
